import { array, option, record } from 'fp-ts';
import { pipe, tupled } from 'fp-ts/function';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { get, nth, overEvery } from 'lodash/fp';
import { createSelector } from 'reselect';
import { Restriction } from '@mews-commander/data-access';
import { TimeUnit } from '@mews/enums';
import { createTimeUnitRangeArray, propIsNotNull } from '@mews-commander/utils';
import { SelectedCell } from '../types';
import { getCellExceptions } from '../utils';
import { getColumnIndexFromProps, getRestrictionsModule, getRowIndexFromProps } from './common';
import { getDateRange } from './filters';

const getMatrix = createSelector(getRestrictionsModule, get('matrix'));

export const getDateRangeArray = createSelector(getDateRange, ({ startDateTime, endDateTime }) =>
    createTimeUnitRangeArray({ startDateTime, endDateTime, timeUnit: TimeUnit.Day })
);

export const getDateFromDateRangeArrayByIndex = createSelector(getColumnIndexFromProps, getDateRangeArray, nth);

export const calculateMaxExceptionsCountOfRow = (rowRestrictions: NonEmptyArray<Restriction[]>, timeUnit: TimeUnit) =>
    pipe(
        rowRestrictions,
        array.map((restrictions: Array<Restriction>) =>
            array.isEmpty(restrictions)
                ? 0
                : pipe(
                      { restrictions, timeUnit },
                      getCellExceptions,
                      record.filterMap(option.fromNullable),
                      record.size
                  )
        ),
        tupled(Math.max)
    );

export const getSelectedCell = createSelector(
    getMatrix,
    ({ selectedCell }: { selectedCell: SelectedCell }) => selectedCell
);

export const isAnyCellSelected = createSelector(
    getSelectedCell,
    overEvery([propIsNotNull('rowIndex'), propIsNotNull('columnIndex')])
);

export const isCellSelected = createSelector(
    getSelectedCell,
    getRowIndexFromProps,
    getColumnIndexFromProps,
    (selectedCell, rowIndex, columnIndex) =>
        selectedCell.rowIndex === rowIndex && selectedCell.columnIndex === columnIndex
);
